import { decisionItems, RAW_VARIANCE } from './data'
import type { DecisionItem, DecisionsState, DecisionStatus, NetSuiteLine } from './types'

export function formatCurrency(value: number, options?: { compact?: boolean }): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: options?.compact ? 0 : 2,
    minimumFractionDigits: options?.compact ? 0 : 2,
  }).format(value)
}

export function formatSignedCurrency(value: number): string {
  if (value < 0) return `(${formatCurrency(Math.abs(value))})`
  return formatCurrency(value)
}

export function buildInitialDecisions(items: DecisionItem[] = decisionItems): DecisionsState {
  return items.reduce<DecisionsState>((acc, item) => {
    acc[item.id] = {
      status: 'pending',
      amount: item.amount,
      note: '',
    }
    return acc
  }, {})
}

export function computeDecisionCounts(decisions: DecisionsState) {
  return Object.values(decisions).reduce(
    (acc, decision) => {
      acc[decision.status] += 1
      return acc
    },
    { pending: 0, approved: 0, modified: 0, rejected: 0 } as Record<DecisionStatus, number>,
  )
}

export function computeResidualVariance(
  decisions: DecisionsState,
  items: DecisionItem[] = decisionItems,
  rawVariance = RAW_VARIANCE,
): number {
  const resolvedImpact = items.reduce((sum, item) => {
    const decision = decisions[item.id]
    if (!decision || (decision.status !== 'approved' && decision.status !== 'modified')) {
      return sum
    }
    return sum + decision.amount * item.impactDirection
  }, 0)
  return rawVariance + resolvedImpact
}

export function areAllDecisionsResolved(decisions: DecisionsState): boolean {
  return Object.values(decisions).every(decision => decision.status !== 'pending')
}

export function getCloseStatus(
  decisions: DecisionsState,
  items: DecisionItem[] = decisionItems,
  rawVariance = RAW_VARIANCE,
) {
  const residual = computeResidualVariance(decisions, items, rawVariance)
  const allResolved = areAllDecisionsResolved(decisions)
  if (!allResolved) {
    return {
      kind: 'blocked' as const,
      label: 'Close blocked',
      description: 'Controller decisions are still pending.',
      residual,
    }
  }
  if (Math.abs(residual) <= 1) {
    return {
      kind: 'reconciled' as const,
      label: 'Reconciled',
      description: 'All variance items are resolved within the $1 tolerance.',
      residual: 0,
    }
  }
  return {
    kind: 'timing' as const,
    label: 'Timing items',
    description: 'All decisions are documented, with residual timing differences carried forward.',
    residual,
  }
}

function csvEscape(value: string | number | undefined): string {
  if (value === undefined || value === null) return ''
  const text = String(value)
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`
  }
  return text
}

function amountText(value: number | undefined): string {
  return value === undefined ? '' : value.toFixed(2)
}

export function buildNetSuiteLines(
  decisions: DecisionsState,
  items: DecisionItem[] = decisionItems,
): NetSuiteLine[] {
  const lines: NetSuiteLine[] = []

  for (const item of items) {
    const decision = decisions[item.id]
    if (!decision || (decision.status !== 'approved' && decision.status !== 'modified')) {
      continue
    }

    const status = decision.status === 'approved' ? 'Approved' : 'Modified'
    const amount = decision.amount

    if (item.id === 3) {
      const payrollHalf = amount / 2
      lines.push(
        {
          externalId: 'EC07-APR25-003A',
          journalDate: '04/15/2025',
          postingPeriod: '4/2025',
          memo: 'EC-07 Apr 2025 - Payroll funding transfer 10100 to 10130 - Apr 15 run',
          accountNumber: '10130',
          accountName: 'Payroll Checking - Pacific Commerce',
          debit: payrollHalf,
          approvalStatus: status,
        },
        {
          externalId: 'EC07-APR25-003A',
          journalDate: '04/15/2025',
          postingPeriod: '4/2025',
          memo: 'EC-07 Apr 2025 - Payroll funding transfer 10100 to 10130 - Apr 15 run',
          accountNumber: '10100',
          accountName: 'Pacific Commerce Bank - Primary Checking',
          credit: payrollHalf,
          approvalStatus: status,
        },
        {
          externalId: 'EC07-APR25-003B',
          journalDate: '04/30/2025',
          postingPeriod: '4/2025',
          memo: 'EC-07 Apr 2025 - Payroll funding transfer 10100 to 10130 - Apr 30 run',
          accountNumber: '10130',
          accountName: 'Payroll Checking - Pacific Commerce',
          debit: payrollHalf,
          approvalStatus: status,
        },
        {
          externalId: 'EC07-APR25-003B',
          journalDate: '04/30/2025',
          postingPeriod: '4/2025',
          memo: 'EC-07 Apr 2025 - Payroll funding transfer 10100 to 10130 - Apr 30 run',
          accountNumber: '10100',
          accountName: 'Pacific Commerce Bank - Primary Checking',
          credit: payrollHalf,
          approvalStatus: status,
        },
      )
      continue
    }

    const templateById: Record<number, [NetSuiteLine, NetSuiteLine]> = {
      1: [
        {
          externalId: 'EC07-APR25-001',
          journalDate: '04/30/2025',
          postingPeriod: '4/2025',
          memo: 'EC-07 Apr 2025 - Missing vendor ACH payments to primary checking 10100',
          accountNumber: '20100',
          accountName: 'Accounts Payable Clearing',
          debit: amount,
          approvalStatus: status,
        },
        {
          externalId: 'EC07-APR25-001',
          journalDate: '04/30/2025',
          postingPeriod: '4/2025',
          memo: 'EC-07 Apr 2025 - Missing vendor ACH payments to primary checking 10100',
          accountNumber: '10100',
          accountName: 'Pacific Commerce Bank - Primary Checking',
          credit: amount,
          approvalStatus: status,
        },
      ],
      2: [
        {
          externalId: 'EC07-APR25-002',
          journalDate: '04/30/2025',
          postingPeriod: '4/2025',
          memo: 'EC-07 Apr 2025 - Customer wire receipts not yet posted - AR clearing',
          accountNumber: '10100',
          accountName: 'Pacific Commerce Bank - Primary Checking',
          debit: amount,
          approvalStatus: status,
        },
        {
          externalId: 'EC07-APR25-002',
          journalDate: '04/30/2025',
          postingPeriod: '4/2025',
          memo: 'EC-07 Apr 2025 - Customer wire receipts not yet posted - AR clearing',
          accountNumber: '12100',
          accountName: 'Accounts Receivable',
          credit: amount,
          approvalStatus: status,
        },
      ],
      4: [
        {
          externalId: 'EC07-APR25-004',
          journalDate: '04/30/2025',
          postingPeriod: '4/2025',
          memo: 'EC-07 Apr 2025 - Partial reversal JE30426: interest overposted to 10100',
          accountNumber: '11020',
          accountName: 'Interest Income - Investments',
          debit: amount,
          approvalStatus: status,
        },
        {
          externalId: 'EC07-APR25-004',
          journalDate: '04/30/2025',
          postingPeriod: '4/2025',
          memo: 'EC-07 Apr 2025 - Partial reversal JE30426: interest overposted to 10100',
          accountNumber: '10100',
          accountName: 'Pacific Commerce Bank - Primary Checking',
          credit: amount,
          approvalStatus: status,
        },
      ],
      5: [
        {
          externalId: 'EC07-APR25-005',
          journalDate: '04/30/2025',
          postingPeriod: '4/2025',
          memo: 'EC-07 Apr 2025 - Partial reversal JE30427: fee overstatement on 10100',
          accountNumber: '10100',
          accountName: 'Pacific Commerce Bank - Primary Checking',
          debit: amount,
          approvalStatus: status,
        },
        {
          externalId: 'EC07-APR25-005',
          journalDate: '04/30/2025',
          postingPeriod: '4/2025',
          memo: 'EC-07 Apr 2025 - Partial reversal JE30427: fee overstatement on 10100',
          accountNumber: '69000',
          accountName: 'Office & Administration - Bank Fees',
          credit: amount,
          approvalStatus: status,
        },
      ],
    }

    const template = templateById[item.id]
    if (template) {
      lines.push(...template)
    }
  }

  return lines
}

export function generateNetSuiteCsv(
  decisions: DecisionsState,
  items: DecisionItem[] = decisionItems,
): string {
  const headers = [
    'External ID',
    'Journal Date',
    'Posting Period',
    'Memo',
    'Account Number',
    'Account Name',
    'Debit',
    'Credit',
    'Created By',
    'Source',
    'Approval Status',
  ]
  const rows = buildNetSuiteLines(decisions, items).map(line => [
    line.externalId,
    line.journalDate,
    line.postingPeriod,
    line.memo,
    line.accountNumber,
    line.accountName,
    amountText(line.debit),
    amountText(line.credit),
    'EC-07 CrossCore AI - Controller: Marlowe Mitchell',
    'Bank Reconciliation Apr 2025 - EC-07',
    line.approvalStatus,
  ])
  return [headers, ...rows].map(row => row.map(csvEscape).join(',')).join('\n')
}

export function generateAuditTrailCsv(
  decisions: DecisionsState,
  items: DecisionItem[] = decisionItems,
): string {
  const headers = [
    'JE Number',
    'Item Type',
    'HITL Priority',
    'Decision',
    'Amount',
    'Controller Note',
    'JE Impact on GL',
    'Timestamp',
    'Controller',
    'Agent',
    'Source Documents',
  ]
  const rows = items.map(item => {
    const decision = decisions[item.id]
    const impactAccount = item.impactDirection < 0 ? 'CR 10100' : 'DR 10100'
    return [
      item.code,
      item.jeType,
      item.priority,
      decision.status.toUpperCase(),
      formatCurrency(decision.amount),
      decision.note || 'N/A',
      `${impactAccount} ${formatCurrency(decision.amount)}`,
      decision.timestamp || '',
      'Marlowe Mitchell',
      'EC-07 CrossCore AI',
      item.sourceDocuments,
    ]
  })
  return [headers, ...rows].map(row => row.map(csvEscape).join(',')).join('\n')
}

export function downloadTextFile(filename: string, content: string, type = 'text/csv'): void {
  const blob = new Blob([content], { type: `${type};charset=utf-8;` })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}
